import React, { useMemo, useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircle2Icon, TrendingUpIcon, ShieldCheckIcon, ZapIcon, ClockIcon, ArrowRightIcon, ArrowLeftIcon, AlertCircleIcon, InfoIcon, AwardIcon, FileTextIcon, DownloadIcon, PenToolIcon } from 'lucide-react';
import { BRAND } from '../../../shared/config/brand';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { financingEligibility, getFinancingOffers, settlementOptions, SettlementSpeed } from '../mocks/financingData';
const formatMYR = (amount: number) => formatCurrencyAmount(amount, {
  currency: 'MYR'
});
export function FinancingApplyWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);
  // Step 3: Offers State
  const [borrowAmount, setBorrowAmount] = useState<number>(financingEligibility.maxAmount);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  // Step 4: Payout State
  const [settlementSpeed, setSettlementSpeed] = useState<SettlementSpeed>('instant');
  const offers = useMemo(() => getFinancingOffers(borrowAmount), [borrowAmount]);
  const selectedOffer = offers.find(o => o.id === selectedOfferId);
  const selectedSettlement = settlementOptions.find(s => s.id === settlementSpeed);
  const handleNext = () => setStep(s => Math.min(s + 1, 6) as any);
  const handleBack = () => setStep(s => Math.max(s - 1, 1) as any);
  return <div className="fixed inset-0 z-50 bg-[#F8F9FA] overflow-y-auto flex flex-col" data-id="element-5048" data-mp="2fyp80lhwfzh8">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10 flex-shrink-0" data-id="element-5049" data-mp="2azug8u1yf0kz">
        <button onClick={() => navigate('/bills/financing')} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors" data-id="element-5050" data-mp="llpmkbqu8b21">
          <XIcon size={20} data-id="element-5051" data-mp="2gom9bdizp4hv" />
        </button>
        <div className="flex items-center gap-3" data-id="element-5052" data-mp="2gpxhdpjgwyes">
          <span className="text-xs text-gray-500 font-medium" data-id="element-5053" data-mp="2lm4dyrpz4xk2">
            Step {step} of 6
          </span>
          <div className="w-64 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5054" data-mp="2qkxocb9ys8g0">
            <div className="h-full bg-[var(--brand-primary)] transition-all duration-300 ease-in-out rounded-full" style={{
            width: `${step / 6 * 100}%`
          }} data-id="element-5055" data-mp="2uo3wxpkjubpn" />
          </div>
        </div>
        <div className="w-10" data-id="element-5056" data-mp="34jp0hs59nk1i" />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col" data-id="element-5057" data-mp="39dt88dmcvw3n">
        <AnimatePresence mode="wait" data-id="element-5058" data-mp="3eblcm746taoq">
          <motion.div key={step} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="flex-1 max-w-4xl w-full mx-auto py-10 px-4 sm:px-6 flex flex-col" data-id="element-5059" data-mp="3eajob0y5urp2">
            {/* STEP 1: Eligibility Gates */}
            {step === 1 && <div className="flex-1 flex flex-col" data-id="element-5060" data-mp="12qc7hoko35qz">
                <div className="text-center mb-10" data-id="element-5061" data-mp="xq6lop12wdgt">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-5062" data-mp="xrhoalisff6c">
                    <ShieldCheckIcon size={32} className="text-green-600" data-id="element-5063" data-mp="1qe2gtpb01xbh" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-5064" data-mp="1lmu5gnq401vo">
                    You're eligible for instant financing
                  </h1>
                  <p className="text-gray-500 max-w-lg mx-auto" data-id="element-5065" data-mp="1gnqxoviqvhk0">
                    Your business meets all the requirements for {BRAND.name}{' '}
                    Financing. Here's a breakdown of your eligibility status.
                  </p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-10" data-id="element-5066" data-mp="1sw45nazjvm64">
                  <div className="divide-y divide-gray-100" data-id="element-5067" data-mp="1xx1lhgx3844g">
                    {financingEligibility.gates.map(gate => <div key={gate.id} className="p-6 flex items-center justify-between" data-id="element-5068" data-mp="1jyk4s35lnrw1">
                        <div className="flex items-center gap-4" data-id="element-5069" data-mp="2cm7n1bjee6fi">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${gate.passed ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-5070" data-mp="2gmi0f99irz4e">
                            <CheckCircle2Icon size={18} data-id="element-5071" data-mp="27sv7dqwp59yw" />
                          </div>
                          <div data-id="element-5072" data-mp="2vj8r8u54o2ie">
                            <div className="font-semibold text-gray-900" data-id="element-5073" data-mp="2vhxomxnf50sv">
                              {gate.label}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5074" data-mp="39oramgjk4htz">
                              Requirement: {gate.requirement}
                            </div>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-5075" data-mp="3jeubf2wx702v">
                          <div className="text-sm font-medium text-gray-900" data-id="element-5076" data-mp="3egiwidrrpzds">
                            {gate.current}
                          </div>
                          <div className={`text-xs font-semibold mt-0.5 ${gate.passed ? 'text-green-600' : 'text-gray-500'}`} data-id="element-5077" data-mp="2qqyh24utpk99">
                            {gate.passed ? 'Passed' : 'Pending'}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>

                <div className="mt-auto flex justify-center" data-id="element-5078" data-mp="fwsm1fvet6oy">
                  <button onClick={handleNext} className="px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm flex items-center gap-2" data-id="element-5079" data-mp="ayh9uyhn6k3k">
                    View my analysis <ArrowRightIcon size={18} data-id="element-5080" data-mp="s1y7fdeechtq" />
                  </button>
                </div>
              </div>}

            {/* STEP 2: GMV Analysis & Signals */}
            {step === 2 && <div className="flex-1 flex flex-col" data-id="element-5081" data-mp="3viy8vvdslu8i">
                <div className="text-center mb-10" data-id="element-5082" data-mp="4bc1brnyd7md">
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-5083" data-mp="4bvi351v9pyt">
                    Your Business Analysis
                  </h1>
                  <p className="text-gray-500 max-w-lg mx-auto" data-id="element-5084" data-mp="e15a6kwpn8mx">
                    We've analyzed your processing history to determine your
                    financing terms. Stronger signals mean better rates.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10" data-id="element-5085" data-mp="3honrjz44l2ch">
                  {/* Score Card */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col items-center justify-center text-center" data-id="element-5086" data-mp="3a5xyxj9j9lzv">
                    <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-5087" data-mp="3080b2wvl5r7s">
                      Underwriting Score
                    </div>
                    <div className="relative w-32 h-32 flex items-center justify-center mb-4" data-id="element-5088" data-mp="2vbt3kznjk7nq">
                      <svg className="w-full h-full transform -rotate-90" data-id="element-5089" data-mp="2r9of0fbya17y">
                        <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-100" data-id="element-5090" data-mp="2mfk3dirqp50j" />
                        <motion.circle initial={{
                      strokeDasharray: '0 1000'
                    }} animate={{
                      strokeDasharray: `${financingEligibility.score / 100 * 351.8} 1000`
                    }} transition={{
                      duration: 1.5,
                      ease: 'easeOut'
                    }} cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-[var(--brand-primary)]" data-id="element-5091" data-mp="23ij1i6thfl0t" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-5092" data-mp="33q600iedxpiy">
                        <span className="text-3xl font-bold text-gray-900" data-id="element-5093" data-mp="2pqwgb8qz0da8">
                          {financingEligibility.score}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-5094" data-mp="2b2msw04wtfey">/ 100</span>
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm font-semibold" data-id="element-5095" data-mp="264uymzgslh7l">
                      <AwardIcon size={14} data-id="element-5096" data-mp="2k1857djsfy7a" />
                      {financingEligibility.scoreTier} Tier
                    </div>
                  </div>

                  {/* GMV Chart & Signals */}
                  <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm p-6" data-id="element-5097" data-mp="2tzfinmevby4j">
                    <div className="flex items-center justify-between mb-6" data-id="element-5098" data-mp="32u2ky9p8juck">
                      <h3 className="font-bold text-gray-900" data-id="element-5099" data-mp="32w6mwm3kqja7">
                        90-Day Volume Trend
                      </h3>
                      <span className="text-sm text-gray-500" data-id="element-5100" data-mp="3cs078rnjbh2j">MYR</span>
                    </div>
                    <div className="h-40 flex items-end gap-1 mb-8" data-id="element-5101" data-mp="37tf8yxruf7gq">
                      {financingEligibility.gmvSeries.map((val, i) => {
                    const max = Math.max(...financingEligibility.gmvSeries);
                    const height = `${val / max * 100}%`;
                    return <motion.div key={i} initial={{
                      height: 0
                    }} animate={{
                      height
                    }} transition={{
                      duration: 0.5,
                      delay: i * 0.01
                    }} className="flex-1 bg-[var(--brand-primary-light)] rounded-t-sm hover:bg-[var(--brand-primary)] transition-colors" data-id="element-5102" data-mp="2v8q966hul9bk" data-mpi={i} />;
                  })}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5103" data-mp="1xkq09nyel8ud">
                      {financingEligibility.signals.map(signal => <div key={signal.id} className="p-4 rounded-xl bg-gray-50 border border-gray-100" data-id="element-5104" data-mp="1nfzt93o57eja">
                          <div className="flex items-start justify-between mb-1" data-id="element-5105" data-mp="1xepdv47fbd38">
                            <div className="text-xs font-medium text-gray-500" data-id="element-5106" data-mp="228a244j7ssp8">
                              {signal.label}
                            </div>
                            {signal.sentiment === 'positive' && <TrendingUpIcon size={14} className="text-green-500" data-id="element-5107" data-mp="2b9hwwcmivbek" />}
                            {signal.sentiment === 'neutral' && <ArrowRightIcon size={14} className="text-gray-400" data-id="element-5108" data-mp="lu1tldva4jwl" />}
                            {signal.sentiment === 'caution' && <AlertCircleIcon size={14} className="text-amber-500" data-id="element-5109" data-mp="dsugagjlhtx0" />}
                          </div>
                          <div className="font-bold text-gray-900 mb-1" data-id="element-5110" data-mp="ns1t92mf3mjl">
                            {signal.value}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-5111" data-mp="wrf49zq6wj6f">
                            {signal.detail}
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>

                <div className="mt-auto flex justify-center gap-4" data-id="element-5112" data-mp="16it09xixn0ut">
                  <button onClick={handleBack} className="px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors shadow-sm" data-id="element-5113" data-mp="1kgr8el6nu11v">
                    Back
                  </button>
                  <button onClick={handleNext} className="px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm flex items-center gap-2" data-id="element-5114" data-mp="241hs5ba2qjlb">
                    See my offers <ArrowRightIcon size={18} data-id="element-5115" data-mp="1gavv0f1lyjq3" />
                  </button>
                </div>
              </div>}

            {/* STEP 3: Offers */}
            {step === 3 && <div className="flex-1 flex flex-col" data-id="element-5116" data-mp="17d1xx1w0rm1v">
                <div className="text-center mb-10" data-id="element-5117" data-mp="1bd2qdqfydxrx">
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-5118" data-mp="1bdvm7wddj2va">
                    Choose your financing
                  </h1>
                  <p className="text-gray-500 max-w-lg mx-auto" data-id="element-5119" data-mp="12co16fzvbyj5">
                    Select the amount you need and choose a repayment plan that
                    fits your cash flow.
                  </p>
                </div>

                {/* Amount Slider */}
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-8" data-id="element-5120" data-mp="20pa468v1bxys">
                  <div className="flex justify-between items-end mb-6" data-id="element-5121" data-mp="1vvxia6hqq59q">
                    <div data-id="element-5122" data-mp="1qxma0073g2tm">
                      <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-5123" data-mp="1qyf7eak5xfz3">
                        Borrow Amount
                      </div>
                      <div className="text-4xl font-bold text-gray-900" data-id="element-5124" data-mp="1gzg6je5n2omn">
                        {formatMYR(borrowAmount)}
                      </div>
                    </div>
                    <div className="text-right" data-id="element-5125" data-mp="180cn292k877m">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-5126" data-mp="181nq26o6laxo">
                        Max available
                      </div>
                      <div className="font-semibold text-gray-900" data-id="element-5127" data-mp="32ducy2qftg5t">
                        {formatMYR(financingEligibility.maxAmount)}
                      </div>
                    </div>
                  </div>
                  <input type="range" min={10000} max={financingEligibility.maxAmount} step={1000} value={borrowAmount} onChange={e => setBorrowAmount(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--brand-primary)]" data-id="element-5128" data-mp="3ew6ewgxqlsly" />
                </div>

                {/* Offers Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-5129" data-mp="30w5ibdoi97ca">
                  {offers.map(offer => <div key={offer.id} onClick={() => setSelectedOfferId(offer.id)} className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all ${selectedOfferId === offer.id ? 'border-[var(--brand-primary)] bg-white shadow-md' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`} data-id="element-5130" data-mp="34zbhy2h4409s">
                      {offer.recommended ? <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap shadow-sm" data-id="element-5131" data-mp="2cbxc2r8ui85m">
                          {offer.highlight.toUpperCase()}
                        </div> : <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-100 text-gray-600 border border-gray-200 text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap" data-id="element-5132" data-mp="1xibwbhp1764c">
                          {offer.highlight.toUpperCase()}
                        </div>}

                      <div className="text-center mb-6 mt-2" data-id="element-5133" data-mp="22hy95q0y6kcm">
                        <h4 className="font-bold text-gray-900 text-xl" data-id="element-5134" data-mp="1esm3h04twuf2">
                          {offer.name}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-5135" data-mp="3lih205sl1b2z">
                          {offer.tagline}
                        </p>
                      </div>

                      <div className="space-y-4 mb-6" data-id="element-5136" data-mp="3qaqqrihyo53o">
                        <div className="flex justify-between items-center text-sm" data-id="element-5137" data-mp="3ciaqgpgg2l6g">
                          <span className="text-gray-500" data-id="element-5138" data-mp="3hgm1v4me13qs">Term</span>
                          <span className="font-semibold text-gray-900" data-id="element-5139" data-mp="3hergxqb49bmp">
                            {offer.termMonths} months
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-5140" data-mp="d1umjc5jaeec">
                          <span className="text-gray-500" data-id="element-5141" data-mp="3v7h623lxywo1">Daily holdback</span>
                          <span className="font-semibold text-gray-900" data-id="element-5142" data-mp="4165e8kufxx8">
                            {offer.holdbackRate}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-5143" data-mp="2ospxreuho9jz">
                          <span className="text-gray-500" data-id="element-5144" data-mp="3gfhua8tchf2g">Total fees</span>
                          <span className="font-semibold text-gray-900" data-id="element-5145" data-mp="3gdomgq6zwosv">
                            {formatMYR(offer.totalFees)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-5146" data-mp="36htw18wj5pfj">
                          <span className="text-gray-500" data-id="element-5147" data-mp="31k1xhw1dr52d">Est. daily</span>
                          <span className="font-semibold text-gray-900" data-id="element-5148" data-mp="2ztm3o7ez9za">
                            ~{formatMYR(offer.estimatedDailyDeduction)}
                          </span>
                        </div>
                      </div>

                      <div className="pt-5 border-t border-gray-100 text-center" data-id="element-5149" data-mp="3pemurkogvih9">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-5150" data-mp="3kgtnspfcakfx">
                          Total repayable
                        </div>
                        <div className="text-2xl font-bold text-[var(--brand-primary)]" data-id="element-5151" data-mp="h0mjcgp3oj63">
                          {formatMYR(offer.totalRepayable)}
                        </div>
                      </div>

                      {selectedOfferId === offer.id && <div className="absolute top-4 right-4 text-[var(--brand-primary)]" data-id="element-5152" data-mp="tb5h777f5f5g">
                          <CheckCircle2Icon size={24} data-id="element-5153" data-mp="y8dqsjei4av3" />
                        </div>}
                    </div>)}
                </div>

                <div className="mt-auto flex justify-center gap-4" data-id="element-5154" data-mp="187392dab7xbv">
                  <button onClick={handleBack} className="px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors shadow-sm" data-id="element-5155" data-mp="1c9hrwxdzax35">
                    Back
                  </button>
                  <button onClick={handleNext} disabled={!selectedOfferId} className="px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5156" data-mp="1qym7s7xawp83">
                    Continue <ArrowRightIcon size={18} data-id="element-5157" data-mp="2zh91yzq4a1q3" />
                  </button>
                </div>
              </div>}

            {/* STEP 4: Approval & Payout */}
            {step === 4 && selectedOffer && <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full" data-id="element-5158" data-mp="3dfqncqsjv24u">
                <div className="text-center mb-10" data-id="element-5159" data-mp="3ieaauvmsklzb">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-5160" data-mp="3icz5wshm186m">
                    <ZapIcon size={32} className="text-green-600" data-id="element-5161" data-mp="3n5sb1hgzqij8" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-5162" data-mp="3s3k44etdgap0">
                    Instant Approval
                  </h1>
                  <p className="text-gray-500" data-id="element-5163" data-mp="vz9a5ojknwu">
                    Your {formatMYR(borrowAmount)} financing is approved. Choose
                    how quickly you want the funds disbursed to your{' '}
                    {BRAND.name} settlement account.
                  </p>
                </div>

                <div className="space-y-4 mb-8" data-id="element-5164" data-mp="1ggqburpv9p3f">
                  {settlementOptions.map(option => <label key={option.id} className={`flex items-start gap-4 p-6 rounded-2xl border-2 cursor-pointer transition-all ${settlementSpeed === option.id ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-light)]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-5165" data-mp="1bhn8dbpjbqxk">
                      <input type="radio" name="settlementSpeed" value={option.id} checked={settlementSpeed === option.id} onChange={() => setSettlementSpeed(option.id as SettlementSpeed)} className="mt-1 w-5 h-5 text-[var(--brand-primary)] border-gray-300 focus:ring-[var(--brand-primary)]" data-id="element-5166" data-mp="1ze19y8zricha" />
                      <div className="flex-1" data-id="element-5167" data-mp="p8gdxbeebinj">
                        <div className="flex justify-between items-start mb-1" data-id="element-5168" data-mp="bicxj1276j60">
                          <div className="font-bold text-gray-900 text-lg" data-id="element-5169" data-mp="ggxy51ld34uz">
                            {option.label}
                          </div>
                          <div className="font-semibold text-gray-900" data-id="element-5170" data-mp="1ivwiwxw4tgd">
                            {option.fee === 0 ? 'Free' : formatMYR(option.fee)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-3" data-id="element-5171" data-mp="3oq7n6syeh58v">
                          {option.description}
                        </p>
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-md text-xs font-medium text-gray-600 border border-gray-200" data-id="element-5172" data-mp="1mx9paiprnmh2">
                          <ClockIcon size={14} data-id="element-5173" data-mp="1rvbgxs19cu45" />
                          ETA: {option.eta}
                        </div>
                      </div>
                    </label>)}
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-10" data-id="element-5174" data-mp="1wz3zxj3tv05t">
                  <h3 className="font-bold text-gray-900 mb-4" data-id="element-5175" data-mp="2au686cw5d1ls">Summary</h3>
                  <div className="space-y-3 text-sm" data-id="element-5176" data-mp="260bw9vms9qx9">
                    <div className="flex justify-between" data-id="element-5177" data-mp="2kpq592mg66c4">
                      <span className="text-gray-500" data-id="element-5178" data-mp="2ksccf15fejtt">Financing amount</span>
                      <span className="font-medium text-gray-900" data-id="element-5179" data-mp="2fqwtpfqq0pel">
                        {formatMYR(borrowAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-5180" data-mp="2pqnm9bvq0uc1">
                      <span className="text-gray-500" data-id="element-5181" data-mp="zd6lbwskjez5">Plan</span>
                      <span className="font-medium text-gray-900" data-id="element-5182" data-mp="zdomn7u38jdx">
                        {selectedOffer.name} ({selectedOffer.termMonths} months)
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-5183" data-mp="wsgxw3ynos2g">
                      <span className="text-gray-500" data-id="element-5184" data-mp="11r20ga1drnu2">Disbursement fee</span>
                      <span className="font-medium text-gray-900" data-id="element-5185" data-mp="15tojpmjzk0fn">
                        {selectedSettlement?.fee === 0 ? 'Free' : formatMYR(selectedSettlement?.fee || 0)}
                      </span>
                    </div>
                    <div className="pt-3 border-t border-gray-100 flex justify-between font-bold text-lg" data-id="element-5186" data-mp="e0fquebq9vhy">
                      <span className="text-gray-900" data-id="element-5187" data-mp="n2xemx716w72">Net payout</span>
                      <span className="text-[var(--brand-primary)]" data-id="element-5188" data-mp="n1magw9obmff">
                        {formatMYR(borrowAmount - (selectedSettlement?.fee || 0))}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-auto flex justify-center gap-4" data-id="element-5189" data-mp="3vgxpo02hjthp">
                  <button onClick={handleBack} className="px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors shadow-sm" data-id="element-5190" data-mp="3ny6irl78j590">
                    Back
                  </button>
                  <button onClick={handleNext} className="flex-1 px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm" data-id="element-5191" data-mp="39yeyx3u1eun3">
                    Review loan agreement
                  </button>
                </div>
              </div>}

            {/* STEP 5: Loan Agreement & Terms */}
            {step === 5 && selectedOffer && <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full" data-id="element-5192" data-mp="2ma5fg8kaqiz4">
                <div className="text-center mb-8" data-id="element-5193" data-mp="2hb18zporazch">
                  <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-5194" data-mp="2hbaylc5w34hn">
                    <FileTextIcon size={32} className="text-blue-600" data-id="element-5195" data-mp="27p9qvy7oxc6j" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-5196" data-mp="2bq3cgkxnuew9">
                    Loan Agreement & Terms
                  </h1>
                  <p className="text-gray-500" data-id="element-5197" data-mp="22nu8yf0t7lsd">
                    Please review the final terms of your financing facility
                    before signing.
                  </p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-8" data-id="element-5198" data-mp="2vd9eip9pgmgw">
                  <div className="p-6 border-b border-gray-100" data-id="element-5199" data-mp="2gon1wvbgq8td">
                    <h3 className="font-bold text-gray-900 mb-4" data-id="element-5200" data-mp="2lmycx9dhupd6">
                      Facility Details
                    </h3>
                    <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm" data-id="element-5201" data-mp="39c93jy14r3dl">
                      <div data-id="element-5202" data-mp="3e2zbnj0xxkbz">
                        <div className="text-gray-500 mb-1" data-id="element-5203" data-mp="3e4anm64jhye6">
                          Principal Amount
                        </div>
                        <div className="font-semibold text-gray-900" data-id="element-5204" data-mp="12m5sx2p2vlfj">
                          {formatMYR(borrowAmount)}
                        </div>
                      </div>
                      <div data-id="element-5205" data-mp="1bg2d4byomnsb">
                        <div className="text-gray-500 mb-1" data-id="element-5206" data-mp="1bieschd22k1o">
                          Total Repayable
                        </div>
                        <div className="font-semibold text-[var(--brand-primary)]" data-id="element-5207" data-mp="jow9vmno09yt">
                          {formatMYR(selectedOffer.totalRepayable)}
                        </div>
                      </div>
                      <div data-id="element-5208" data-mp="sqciboux620e">
                        <div className="text-gray-500 mb-1" data-id="element-5209" data-mp="srnlpnkgd7qz">Facility Term</div>
                        <div className="font-semibold text-gray-900" data-id="element-5210" data-mp="npy8c19rchzq">
                          {selectedOffer.termMonths} months
                        </div>
                      </div>
                      <div data-id="element-5211" data-mp="1z6y1ez3xj0ut">
                        <div className="text-gray-500 mb-1" data-id="element-5212" data-mp="1z5mwgvyqzn24">
                          Daily Holdback Rate
                        </div>
                        <div className="font-semibold text-gray-900" data-id="element-5213" data-mp="2o7dkoptkptxf">
                          {selectedOffer.holdbackRate}% of daily settlement
                        </div>
                      </div>
                      <div data-id="element-5214" data-mp="2wznxmevusdid">
                        <div className="text-gray-500 mb-1" data-id="element-5215" data-mp="2wzxm1y192cly">
                          Disbursement Account
                        </div>
                        <div className="font-semibold text-gray-900" data-id="element-5216" data-mp="31yipe6bstcem">
                          {BRAND.name} Settlement Account
                        </div>
                      </div>
                      <div data-id="element-5217" data-mp="3by9h608z5db0">
                        <div className="text-gray-500 mb-1" data-id="element-5218" data-mp="3buu6raq8wsfx">
                          Disbursement Speed
                        </div>
                        <div className="font-semibold text-gray-900" data-id="element-5219" data-mp="1hjfa09ythsqo">
                          {selectedSettlement?.label}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 border-b border-gray-100 bg-gray-50" data-id="element-5220" data-mp="1e1rmusoqjkoh">
                    <h3 className="font-bold text-gray-900 mb-4" data-id="element-5221" data-mp="19a16a4z7g0om">
                      Repayment Plan
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5222" data-mp="14bpwtvctns6x">
                      Repayment is fully automated. {BRAND.name} will deduct{' '}
                      {selectedOffer.holdbackRate}% from your daily card
                      processing settlements until the Total Repayable amount of{' '}
                      {formatMYR(selectedOffer.totalRepayable)} is fully
                      satisfied.
                    </p>
                    <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-100" data-id="element-5223" data-mp="1o10az4wx652x">
                      <AlertCircleIcon size={16} className="flex-shrink-0" data-id="element-5224" data-mp="ckack7yqmhjv" />
                      <span data-id="element-5225" data-mp="7l75yj34039s">
                        If daily settlements are insufficient, the facility term
                        may be extended automatically without penalty.
                      </span>
                    </div>
                  </div>

                  <div className="p-6" data-id="element-5226" data-mp="2x1f97u26lu20">
                    <h3 className="font-bold text-gray-900 mb-4" data-id="element-5227" data-mp="3aqpj94jc261x">
                      Malaysia Business Financing Disclosure
                    </h3>
                    <div className="text-xs text-gray-500 space-y-3 h-32 overflow-y-auto pr-2 custom-scrollbar" data-id="element-5228" data-mp="35yy03f2ib2lt">
                      <p data-id="element-5229" data-mp="3kr9kw8arlcko">
                        This facility is provided by {BRAND.name} ("The
                        Financier") to the merchant ("The Customer") solely for
                        business and commercial purposes. This is not a consumer
                        credit facility and is not governed by the Consumer
                        Protection Act 1999.
                      </p>
                      <p data-id="element-5230" data-mp="1zdnbmyz7oqtw">
                        By accepting these terms, the Customer irrevocably
                        authorizes the Financier to deduct the agreed holdback
                        percentage from daily settlement batches processed
                        through the Financier's acquiring network.
                      </p>
                      <p data-id="element-5231" data-mp="11brs737f3p7">
                        The Customer acknowledges that the total repayable
                        amount includes a fixed factor fee. There are no
                        compounding interest charges, late fees, or early
                        repayment penalties. The facility is considered in
                        default only if the Customer ceases processing with the
                        Financier or intentionally diverts processing volume to
                        evade repayment.
                      </p>
                      <p data-id="element-5232" data-mp="3icbh7ts3rdd0">
                        Governing Law: This agreement shall be governed by and
                        construed in accordance with the laws of Malaysia.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Signatures required */}
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6" data-id="element-5233" data-mp="2kh8v9eo6im1u">
                  <div className="flex items-center justify-between mb-4" data-id="element-5234" data-mp="2gf42sj9gvvgs">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-5235" data-mp="2ghhzewjspfqg">
                      <PenToolIcon size={18} className="text-[var(--brand-primary)]" data-id="element-5236" data-mp="2bi5a855gbwkp" />
                      Signatures required
                    </h3>
                    <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full" data-id="element-5237" data-mp="2ze9ojmm0qp2f">
                      2 of 2 required
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-5" data-id="element-5238" data-mp="3rukcviqllp6e">
                    This facility requires 2 authorised signatures per your
                    mandate on file with {BRAND.name}.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5239" data-mp="3uf8mfdnrl67j">
                    {[{
                  label: 'Authorised Signatory 1',
                  role: 'Director'
                }, {
                  label: 'Authorised Signatory 2',
                  role: 'Director / Company Secretary'
                }].map(s => <div key={s.label} className="p-4 rounded-xl border border-dashed border-gray-300 bg-gray-50" data-id="element-5240" data-mp="h7xcqwxniyg2">
                        <div className="h-12 mb-3 border-b border-gray-300" data-id="element-5241" data-mp="qzl46xu7kfhj" />
                        <div className="font-semibold text-gray-900 text-sm" data-id="element-5242" data-mp="vxmkjb0920pb">
                          {s.label}
                        </div>
                        <div className="flex items-center justify-between mt-0.5" data-id="element-5243" data-mp="zwvd4cjjzsja">
                          <span className="text-xs text-gray-500" data-id="element-5244" data-mp="14ykmzov1i05s">
                            {s.role}
                          </span>
                          <span className="text-xs font-medium text-amber-600" data-id="element-5245" data-mp="n9p9bgs2ua2b">
                            Awaiting signature
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Download loan terms document */}
                <button onClick={() => {
              const blob = new Blob([`${BRAND.name} BUSINESS FINANCING — LOAN AGREEMENT\n\n` + `Lender: ${BRAND.name} Bank Berhad\n` + `Registered office: ${BRAND.address.oneLine}\n` + `SWIFT: ${BRAND.bank.swift}\n\n` + `Principal: ${formatMYR(borrowAmount)}\n` + `Plan: ${selectedOffer.name} (${selectedOffer.termMonths} months)\n` + `Daily holdback: ${selectedOffer.holdbackRate}%\n` + `Total repayable: ${formatMYR(selectedOffer.totalRepayable)}\n` + `Total fees: ${formatMYR(selectedOffer.totalFees)}\n\n` + `Authorised signatures required: 2\n\n` + `This facility is provided for business/commercial purposes only and is\n` + `governed by the laws of Malaysia and Bank Negara Malaysia guidelines.`], {
                type: 'text/plain'
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${BRAND.shortCode}-Loan-Agreement.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            }} className="w-full bg-white p-4 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors flex items-center gap-4 text-left group mb-10" data-id="element-5246" data-mp="8dhs8pbytor9">
                  <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0 text-blue-600 group-hover:bg-blue-100 transition-colors" data-id="element-5247" data-mp="hu1k643dwn9y">
                    <DownloadIcon size={18} data-id="element-5248" data-mp="cxb43grkxroj" />
                  </div>
                  <div data-id="element-5249" data-mp="8uesia0givu3">
                    <div className="font-semibold text-gray-900 text-sm" data-id="element-5250" data-mp="8vpwoaxte5lq">
                      Download loan agreement (PDF)
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-5251" data-mp="3v8eq4tbipso4">
                      Full loan terms &amp; Malaysia disclosure · 1.2 MB
                    </div>
                  </div>
                </button>

                <div className="mt-auto flex justify-center gap-4" data-id="element-5252" data-mp="3l9yvigda819v">
                  <button onClick={handleBack} className="px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors shadow-sm" data-id="element-5253" data-mp="3h9os9bgvjoyp">
                    Back
                  </button>
                  <button onClick={handleNext} className="flex-1 px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm flex items-center justify-center gap-2" data-id="element-5254" data-mp="25sc2z8c3sq1v">
                    <PenToolIcon size={18} data-id="element-5255" data-mp="1i28b1y8a2eqp" />
                    Sign & accept agreement
                  </button>
                </div>
              </div>}

            {/* STEP 6: Success */}
            {step === 6 && selectedOffer && <div className="flex-1 flex flex-col items-center justify-center max-w-lg mx-auto w-full text-center" data-id="element-5256" data-mp="1rtw05shd3jp0">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 260,
              damping: 20,
              delay: 0.1
            }} className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-8" data-id="element-5257" data-mp="uat2hnpwr170">
                  <CheckCircle2Icon size={48} className="text-green-600" data-id="element-5258" data-mp="3sy09j5ahx5cn" />
                </motion.div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-5259" data-mp="bl7bvyvc63eq">
                  Funds are on the way!
                </h1>
                <p className="text-lg text-gray-600 mb-8" data-id="element-5260" data-mp="kgdiv16ceriu">
                  Your {formatMYR(borrowAmount)} financing has been approved and
                  will be disbursed to your {BRAND.name} settlement account{' '}
                  <strong className="text-gray-900" data-id="element-5261" data-mp="uetcbasuecvi">
                    {selectedSettlement?.eta.toLowerCase()}
                  </strong>
                  .
                </p>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 w-full mb-10 text-left" data-id="element-5262" data-mp="190gxmzas7nfy">
                  <div className="flex items-start gap-3 mb-4" data-id="element-5263" data-mp="1dys4r29ozjuh">
                    <InfoIcon size={20} className="text-[var(--brand-primary)] flex-shrink-0 mt-0.5" data-id="element-5264" data-mp="z2t0y3tm85v5" />
                    <div data-id="element-5265" data-mp="143qjwimg93xp">
                      <h4 className="font-bold text-gray-900 mb-1" data-id="element-5266" data-mp="qair4nmmu2ef">
                        What happens next?
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-5267" data-mp="v20oz2tczmv6">
                        Repayment is fully automatic. Starting tomorrow, we will
                        deduct {selectedOffer.holdbackRate}% from your daily
                        settlements until the total repayable amount of{' '}
                        {formatMYR(selectedOffer.totalRepayable)} is reached.
                      </p>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-gray-100 flex justify-between items-center text-sm" data-id="element-5268" data-mp="bet0gc6i513d">
                    <span className="text-gray-500" data-id="element-5269" data-mp="3t4ke2pb6zb2g">Facility Reference</span>
                    <span className="font-mono font-medium text-gray-900" data-id="element-5270" data-mp="3t6ogf2tg020m">
                      CIMB-SME-{Math.floor(10000 + Math.random() * 90000)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 w-full" data-id="element-5271" data-mp="35eznbk8sr9im">
                  <button onClick={() => navigate('/bills/financing')} className="flex-1 px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-colors shadow-sm" data-id="element-5272" data-mp="35h2jyn4gg0wb">
                    Back to Financing
                  </button>
                  <button onClick={() => navigate('/bills/financing')} className="flex-1 px-8 py-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-xl font-semibold transition-colors shadow-sm" data-id="element-5273" data-mp="umexdetbpxaf">
                    View Facility
                  </button>
                </div>
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}